import { GoogleGenerativeAI } from '@google/generative-ai';
import { GEMINI_API, IMAGE_GENERATION_CONFIG } from '../config/api';

interface GenerationResponse {
  id: string;
  status: 'completed' | 'failed';
  images: string[];
  error?: string;
}

class GeminiService {
  private genAI: GoogleGenerativeAI | null = null;

  private getClient(): GoogleGenerativeAI {
    if (!this.genAI) {
      const apiKey = GEMINI_API.apiKey;
      if (!apiKey) {
        console.error('Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY environment variable.');
        throw new Error('Gemini API key is not configured');
      }
      console.log('Initializing Gemini with API key (first 10 chars):', apiKey.substring(0, 10) + '...');
      this.genAI = new GoogleGenerativeAI(apiKey);
    }
    return this.genAI;
  }

  async generateImages(
    action: string,
    userProvidedImages: string[] = []
  ): Promise<GenerationResponse> {
    try {
      const client = this.getClient();
      // Use gemini-2.5-flash-image model for image generation
      const model = client.getGenerativeModel({
        model: GEMINI_API.model
      });

      // Use only user-provided images (no default reference images)
      const allReferenceImages = userProvidedImages;

      // Build prompt based on whether we have reference images
      let prompt: string;
      const parts: any[] = [];

      if (allReferenceImages.length > 0) {
        // For image editing with reference images
        prompt = `Using the provided reference images, generate a new high-resolution, ultra-detailed photorealistic image of this person ${action}. `;
        prompt += `CRITICAL: Maintain the person's exact facial features, skin tone, body type, and physical build from the reference images. `;
        prompt += `However, you are FREE to change the outfit, clothing, hairstyle, and accessories to match the action/situation described. `;
        prompt += `CRITICAL: The background MUST be completely transparent/blank white. Only the person should be visible, isolated on a pure white or transparent background. `;
        prompt += `NO background elements, NO scenery, NO environment - just the person isolated with appropriate clothing and styling for the situation. `;
        prompt += `Make it ultra high quality, sharp, professional photography with maximum detail, clarity, and perfect isolation for compositing. Use the highest possible resolution and quality.`;

        // Add all reference images first
        for (const refImage of allReferenceImages) {
          // Extract base64 data from data URL
          const base64Data = refImage.split(',')[1];
          parts.push({
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Data
            }
          });
        }

        // Add text prompt after images
        parts.push({ text: prompt });

        console.log(`Generating sticker with ${allReferenceImages.length} user-provided reference images`);
      } else {
        // No reference images - cannot generate
        console.error('No reference images provided');
        return {
          id: `gen-${Date.now()}`,
          status: 'failed',
          images: [],
          error: 'Please upload a photo of yourself first to generate stickers'
        };
      }

      // Generate content using gemini-2.5-flash-image model
      const result = await model.generateContent(parts);

      const response = result.response;
      const images: string[] = [];

      // Extract generated images from the response
      if (response.candidates && response.candidates[0]) {
        const candidate = response.candidates[0];
        if (candidate.content && candidate.content.parts) {
          for (const part of candidate.content.parts) {
            // Check for inline image data in the response
            if (part.inlineData && part.inlineData.data) {
              // Convert binary data to base64 data URL
              const mimeType = part.inlineData.mimeType || 'image/png';
              const dataUrl = `data:${mimeType};base64,${part.inlineData.data}`;
              images.push(dataUrl);
              console.log('Successfully generated image');
            } else if (part.text) {
              console.log('Gemini response text:', part.text);
            }
          }
        }
      }

      // If no images were generated, return an error
      if (images.length === 0) {
        console.error('No images generated in response');
        return {
          id: `gen-${Date.now()}`,
          status: 'failed',
          images: [],
          error: 'No images were generated. The model may need different prompting or configuration.'
        };
      }

      return {
        id: `gen-${Date.now()}`,
        status: 'completed',
        images
      };

    } catch (error) {
      console.error('Error generating images:', error);
      let errorMessage = 'Failed to generate images';

      if (error instanceof Error) {
        errorMessage = error.message;

        // Provide helpful error messages
        if (errorMessage.includes('403')) {
          errorMessage = 'API key authentication failed. Please check your Gemini API key.';
        } else if (errorMessage.includes('400')) {
          errorMessage = 'Invalid request format. Please try again.';
        } else if (errorMessage.includes('429')) {
          errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
        }
      }

      return {
        id: `gen-${Date.now()}`,
        status: 'failed',
        images: [],
        error: errorMessage
      };
    }
  }
}

export default new GeminiService();